import {
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useContext, useMemo } from "react";
import { AIRDROP_VAULT_STRINGS } from "./constants";
import {
  Claims,
  Holdings,
  Points,
  VaultAprReports,
} from "@components/VaultTabs";
import { useAccount } from "wagmi";
import { vaultsContractConfig } from "@config/contractsConfig";
import { mainnet } from "viem/chains";
import { formatNumber, numberToCurrency } from "utils";
import useTokenBalance from "@hooks/useTokenBalance";
import useVaultClaims from "@hooks/useVaultClaims";
import { useQuery } from "@tanstack/react-query";
import { getRewardsPointdata } from "services";
import { AirdropVaultPageContext } from "@providers/AirdropVaultProvider";
import { getTagBasedOnType } from "@components/Sidebar/Sidebar";
import { SidebarTag } from "@constants/common";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";

import useEthConversion from "@hooks/useEthConversion";

const TABS_DATA = AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.TABS;
const PORTFOLIO_POINTS_DATA = AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS;

const Portfolio = () => {
  const { address } = useAccount();

  const { xTokenVaultBalance } = useTokenBalance();
  const { cmsAllocations } = useContext(AirdropVaultPageContext);

  const claims = useVaultClaims();

  const conversionsDataAgEth = useAgEthExchangeRate();

  const { valueInEth: erRsEthToEth } = useEthConversion(
    conversionsDataAgEth.toERC20
  );

  const holdingValue = xTokenVaultBalance
    ? formatNumber(Number(xTokenVaultBalance), false, 4, true)
    : "0";

  const holdingValueInEth =
    erRsEthToEth !== "-" && xTokenVaultBalance
      ? formatNumber(
          Number(xTokenVaultBalance) * Number(erRsEthToEth),
          false,
          4,
          true
        )
      : "-";

  const { data: response, isLoading } = useQuery({
    queryKey: ["rewardsData", address],
    queryFn: () => getRewardsPointdata(address as string),

    enabled: !!address,
  });

  const getValue = useCallback(
    (key: string) => {
      let value;
      const vaultData =
        response?.data?.[
          vaultsContractConfig[mainnet.id].interactionPool.address
        ] || null;
      if (vaultData) {
        const { km, scrollPoints, karakPoints, lineaPoints, zircuitPoints } =
          vaultData;
        switch (key) {
          case "kernel": {
            value = km && !isNaN(km) ? km / 1000 : "-";
            break;
          }
          case "linea": {
            value = lineaPoints ?? "-";
            break;
          }
          case "scroll": {
            value = scrollPoints ?? "-";
            break;
          }
          case "karak": {
            value = karakPoints ?? "-";
            break;
          }
          case "zircuitPoints": {
            value = zircuitPoints ?? "-";
            break;
          }
        }
      }

      if (value && value !== "-") {
        const precision = +value > 1 ? 2 : 4;
        return numberToCurrency((+value).toFixed(precision), precision);
      }
      return "-";
    },
    [response]
  );

  const data = useMemo(() => {
    const supportedPoints = PORTFOLIO_POINTS_DATA.supported.map((item) => {
      const value = getValue(item.key);

      return {
        ...item,
        value,
        valueLoading: isLoading,
      };
    });
    return {
      supported: supportedPoints,
      walletConnect: PORTFOLIO_POINTS_DATA.walletConnect,
    };
  }, [isLoading, getValue]);

  return (
    <Flex
      flexDir="column"
      gap="16px"
      w="100%"
      maxW={{ md: "736px", xl: "736px" }}
    >
      <Flex
        flexDir="column"
        pt="4px"
        pb="10px"
        gap="40px"
        borderRadius="24px"
        bg="#EBF9F9"
      >
        <Tabs>
          <TabList fontWeight="600" lineHeight="24px" overflowX="auto">
            {TABS_DATA.map((data) => (
              <Tab
                fontSize={{ base: "12px", xl: "14px" }}
                key={data.key}
                py="8px"
              >
                {data?.isNew && getTagBasedOnType(SidebarTag.INDICATOR)}
                <Text style={{ textWrap: "nowrap" }}>{data.title}</Text>
              </Tab>
            ))}
          </TabList>

          <TabPanels mt={{ base: "10px", xl: "12px" }}>
            <TabPanel p="10px">
              <Holdings
                balance={holdingValue}
                balanceEth={holdingValueInEth}
                symbol={vaultsContractConfig[mainnet.id].xToken.symbol}
              />
            </TabPanel>
            <TabPanel p="10px">
              <Points data={data} />
            </TabPanel>
            <TabPanel p="6px">
              <Claims claims={claims} />
            </TabPanel>
            <TabPanel p="6px">
              <VaultAprReports
                cmsAllocations={cmsAllocations?.allocations || []}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  );
};

export default Portfolio;
